import { loadStripe } from "@stripe/stripe-js";
import CONFIG from "config/config";
import { useMemo } from "react";

// Country → Stripe publishable key config slot. Mirrors the per-country
// secret keys in oppizi-api's StripePaymentGatewayFactory: CA settles
// through the US Stripe account (PAD-30943) and shares its publishable
// key.
const COUNTRY_TO_PUBLISHABLE_KEY = {
  US: "PUBLIC_KEY_US",
  CA: "PUBLIC_KEY_US",
  AU: "PUBLIC_KEY_AU",
  UK: "PUBLIC_KEY_UK",
  FR: "PUBLIC_KEY_FR",
  BE: "PUBLIC_KEY_BE",
  NZ: "PUBLIC_KEY_NZ",
  NL: "PUBLIC_KEY_NL",
  ES: "PUBLIC_KEY_ES",
  DE: "PUBLIC_KEY_DE",
};

/**
 * Load a Stripe.js instance scoped to the country's publishable key.
 *
 * Returns `{ stripePromise, error }`:
 *
 *   - `stripePromise === null && error === null` — country not yet
 *     known. Caller should hold the checkout in a loading state.
 *   - `stripePromise === null && error !== null` — country is known
 *     but the publishable key mapping or env var is missing. Caller
 *     should surface the error to the user (NOT silently fall back to
 *     the US key — that was the PAD-30943 / PAD-30989 cross-account
 *     bug).
 *   - `stripePromise !== null && error === null` — Stripe.js is
 *     loading for the correct per-country account.
 *
 * Throwing inside `useMemo` would unmount the entire checkout subtree
 * (no ErrorBoundary in the checkout tree today, per Greptile review on
 * PR #2168), so the hook returns the error instead and lets the
 * wrapper render a banner.
 */
export const useCountryStripe = (countryCode) => {
  return useMemo(() => {
    if (!countryCode) return { stripePromise: null, error: null };

    const configKey = COUNTRY_TO_PUBLISHABLE_KEY[countryCode];
    if (!configKey) {
      return {
        stripePromise: null,
        error: new Error(
          `No Stripe publishable key mapping for country "${countryCode}". Add it to COUNTRY_TO_PUBLISHABLE_KEY in useStripe.js.`
        ),
      };
    }

    const publishableKey = CONFIG.STRIPE[configKey];
    if (!publishableKey) {
      return {
        stripePromise: null,
        error: new Error(
          `Missing Stripe publishable key env var for country "${countryCode}" (config slot "${configKey}"). Check REACT_APP_PUBLIC_STRIPE_KEY_${
            countryCode === "CA" ? "US" : countryCode
          } on the deployed Netlify site.`
        ),
      };
    }

    return { stripePromise: loadStripe(publishableKey), error: null };
  }, [countryCode]);
};
